import { baseApi, API_TAGS, createCacheTags, createEndpointBuilder, type Params } from '@/shared/api';
import { createFilesFormData, createMultipartFormData, handleFileDownloadResponse } from '@/shared/lib';

import {
  receiveAttachmentSegment,
  receiveConfirmArrivalSegment,
  receiveConfirmContextSegment,
  receiveConvoyConfirmSegment,
  receiveConvoyContextSegment,
  receiveShipmentAttachmentsSegment,
  RECEIVE_RELEASE_SEGMENTS
} from '../config/constants';
import type {
  ReceiveAttachmentDownloadArgs,
  ReceiveAttachmentDto,
  ReceiveAttachmentUploadArgs
} from '../model/attachment-types';
import type {
  ReceiveConfirmArrivalMutationArgs,
  ReceiveConfirmArrivalResultDto,
  ReceiveConfirmContextDto,
  ReceiveReleaseContextDto,
  ReceiveConfirmContextQueryArgs
} from '../model/confirm-types';
import type {
  ReceiveConvoyConfirmMutationArgs,
  ReceiveConvoyConfirmResultDto,
  ReceiveConvoyContextDto,
  ReceiveConvoyContextQueryArgs
} from '../model/convoy-types';
import type {
  ReceiveReleaseContextQueryArgs,
  ReceiveReleaseLookupsQueryArgs,
  ReceiveReleaseMutationArgs,
  ReceiveReleaseResultDto
} from '../model/release-types';

const receiveEndpoint = createEndpointBuilder('/api/v2/receive');

/**
 * Confirm Arrival & Condition Recording — the pair behind the "Confirm receipt
 * of goods" modal:
 *
 *   open modal          → getReceiveConfirmContext (no `targetLocationId`)
 *   change location     → getReceiveConfirmContext (with `targetLocationId`) —
 *                         re-evaluates storage areas and warnings only
 *   submit              → confirmReceiveArrival
 *
 * The write invalidates the whole `Receive` tag: a receipt consumes incoming
 * balances, so every dashboard/history/losses query that touched this site is
 * now stale. There is **no idempotency key** — a failed submit must re-read the
 * context before being retried, which the invalidation also arranges.
 *
 * Alongside them, the attachment endpoints: documents ride along with the
 * receipt as multipart, and can be downloaded or added later on their own.
 */
export const receiveConfirmApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getReceiveConfirmContext: builder.query<ReceiveConfirmContextDto, ReceiveConfirmContextQueryArgs>({
      query: ({ shipmentId, ...params }) => ({
        url: receiveEndpoint(receiveConfirmContextSegment(shipmentId)),
        params
      }),
      providesTags: (_result, _error, args) =>
        createCacheTags({
          baseTag: API_TAGS.RECEIVE,
          params: args as unknown as Params
        })
    }),

    confirmReceiveArrival: builder.mutation<ReceiveConfirmArrivalResultDto, ReceiveConfirmArrivalMutationArgs>({
      query: ({ shipmentId, body, files, attachmentTypes, attachmentComment, ...params }) => {
        const url = receiveEndpoint(receiveConfirmArrivalSegment(shipmentId));

        if (!files?.length) {
          return { url, method: 'POST', params, body };
        }

        return {
          url,
          method: 'POST',
          params,
          body: createMultipartFormData(body, files, { attachmentType: attachmentTypes, comment: attachmentComment })
        };
      },
      // Both forms: `createCacheTags` provides `{ type, id: 'Receive' }`, while
      // endpoints that tag by hand provide the bare type.
      invalidatesTags: [API_TAGS.RECEIVE, { type: API_TAGS.RECEIVE, id: API_TAGS.RECEIVE }]
    }),

    /**
     * Receive Convoy — the same pair at convoy level: every truck of the convoy
     * in one context, and one write that books them all or nothing.
     */
    getReceiveConvoyContext: builder.query<ReceiveConvoyContextDto, ReceiveConvoyContextQueryArgs>({
      query: ({ convoyId, ...params }) => ({
        url: receiveEndpoint(receiveConvoyContextSegment(convoyId)),
        params
      }),
      providesTags: (_result, _error, args) =>
        createCacheTags({
          baseTag: API_TAGS.RECEIVE,
          params: args as unknown as Params
        })
    }),

    confirmReceiveConvoy: builder.mutation<ReceiveConvoyConfirmResultDto, ReceiveConvoyConfirmMutationArgs>({
      query: ({ convoyId, body, ...params }) => ({
        url: receiveEndpoint(receiveConvoyConfirmSegment(convoyId)),
        method: 'POST',
        params,
        body
      }),
      invalidatesTags: [API_TAGS.RECEIVE, { type: API_TAGS.RECEIVE, id: API_TAGS.RECEIVE }]
    }),

    /**
     * Receive and Release, second half: releases the receipts confirm-arrival
     * has just created (`releaseContext.receiveShipmentIds`).
     */
    releaseReceivedGoods: builder.mutation<ReceiveReleaseResultDto, ReceiveReleaseMutationArgs>({
      query: ({ body, files, attachmentTypes, attachmentComment, ...params }) => {
        const url = receiveEndpoint(RECEIVE_RELEASE_SEGMENTS.RELEASE);

        if (!files?.length) {
          return { url, method: 'POST', params, body };
        }

        return {
          url,
          method: 'POST',
          params,
          body: createMultipartFormData(body, files, { attachmentType: attachmentTypes, comment: attachmentComment })
        };
      },
      invalidatesTags: [API_TAGS.RECEIVE, { type: API_TAGS.RECEIVE, id: API_TAGS.RECEIVE }]
    }),

    /**
     * The release prefill for receipts already booked. The backend has no
     * idempotency key: after a refused release the ceilings may have moved, so
     * the form re-reads them before a retry. Not cached — it is only ever read
     * right after a write changed the answer.
     */
    getReceiveReleaseContext: builder.query<ReceiveReleaseContextDto, ReceiveReleaseContextQueryArgs>({
      query: ({ receiveShipmentIds, ...params }) => ({
        url: receiveEndpoint(RECEIVE_RELEASE_SEGMENTS.CONTEXT),
        params: { ...params, receiveShipmentIds: receiveShipmentIds.join(',') }
      }),
      keepUnusedDataFor: 0
    }),

    getReceiveReleaseLookups: builder.query<string[], ReceiveReleaseLookupsQueryArgs>({
      query: (params) => ({ url: receiveEndpoint(RECEIVE_RELEASE_SEGMENTS.LOOKUPS), params })
    }),

    /** Saves the file to disk through the browser; the query itself carries no useful payload. */
    downloadReceiveAttachment: builder.query<Blob, ReceiveAttachmentDownloadArgs>({
      query: ({ shipmentId, attachmentId, projectId, siteId }) => ({
        url: receiveEndpoint(receiveAttachmentSegment(shipmentId, attachmentId)),
        params: { projectId, siteId },
        responseHandler: (response) => handleFileDownloadResponse(response, 'attachment.pdf'),
        cache: 'no-cache'
      })
    }),

    /**
     * Documents for a receipt that is already booked — the Receive History
     * dialog. Answers with every attachment the receipt now carries, so the
     * caller can render the new list without a second call.
     */
    uploadReceiveShipmentAttachments: builder.mutation<ReceiveAttachmentDto[], ReceiveAttachmentUploadArgs>({
      query: ({ shipmentId, projectId, siteId, files, attachmentTypes, comment }) => ({
        url: receiveEndpoint(receiveShipmentAttachmentsSegment(shipmentId)),
        method: 'POST',
        params: { projectId, siteId },
        body: createFilesFormData(files, { attachmentType: attachmentTypes, comment })
      }),
      invalidatesTags: [API_TAGS.RECEIVE, { type: API_TAGS.RECEIVE, id: API_TAGS.RECEIVE }]
    })
  })
});

export const {
  useGetReceiveConfirmContextQuery,
  useLazyGetReceiveConfirmContextQuery,
  useConfirmReceiveArrivalMutation,
  useReleaseReceivedGoodsMutation,
  useLazyGetReceiveReleaseContextQuery,
  useGetReceiveReleaseLookupsQuery,
  useLazyDownloadReceiveAttachmentQuery,
  useUploadReceiveShipmentAttachmentsMutation,
  useGetReceiveConvoyContextQuery,
  useConfirmReceiveConvoyMutation
} = receiveConfirmApi;
